import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';

import { userLegalTypeGuard } from './_helpers/user-legal-type-check.guard';
import { CLAIM_BASE_URL } from './claims/constants';
import { BUSINESS_CLAIM_BASE_URL } from './claims-business/constants';
import { UserLegalType } from './enums/user-legal-type.enum';
import { ANIMAL_INSURANCE_BASE } from './insurance-flows/animal/constants';
import {
  BUILDING_INSURANCE_SERVICE_TOKEN,
  BuildingService,
} from './insurance-flows/building/building.service';
import {
  BUILDING_INSURANCE_BASE,
  OLD_BUILDING_INSURANCE_BASE,
} from './insurance-flows/building/constants';
import { BUSINESS_ACCIDENT_FORM_BASE } from './insurance-flows/business-insurances/business-accident/constants';
import { BusinessBuildingService } from './insurance-flows/business-insurances/business-building/business-building.service';
import { BUSINESS_BUILDING_FORM_URL } from './insurance-flows/business-insurances/business-building/constants';
import {
  BUSINESS_CONSTRUCTION_INSURANCE_BASE,
  BUSINESS_CONSTRUCTION_PROVIDERS,
} from './insurance-flows/business-insurances/business-construction/constants';
import {
  BUSINESS_CONSTRUCTION_WARRANTY_INSURANCE_BASE,
  BUSINESS_CONSTRUCTION_WARRANTY_PROVIDERS,
} from './insurance-flows/business-insurances/business-construction-warranty/constants';
import {
  BUSINESS_CYBERSECURITY_INSURANCE_BASE,
  BUSINESS_CYBERSECURITY_PROVIDERS,
} from './insurance-flows/business-insurances/business-cybersecurity/constants';
import { BUSINESS_HEALTH_FORM_BASE } from './insurance-flows/business-insurances/business-health/constants';
import {
  BUSINESS_LEGAL_PROTECTION_INSURANCE_BASE,
  BUSINESS_LEGAL_PROTECTION_PROVIDERS,
} from './insurance-flows/business-insurances/business-legal-protection/constants';
import {
  BUSINESS_RC_PROFESSIONAL_INSURANCE_BASE,
  BUSINESS_RC_PROFESSIONAL_PROVIDERS,
} from './insurance-flows/business-insurances/business-rc-professional/constants';
import {
  BUSINESS_RENTAL_INSURANCE_BASE,
  BUSINESS_RENTAL_PROVIDERS,
} from './insurance-flows/business-insurances/business-rental/constants';
import { BUSINESS_RETIREMENT_FORM_BASE } from './insurance-flows/business-insurances/business-retirement/constants';
import { BUSINESS_THINGS_FORM_BASE } from './insurance-flows/business-insurances/business-things/constants';
import {
  BUSINESS_TRANSPORT_INSURANCE_BASE,
  BUSINESS_TRANSPORT_PROVIDERS,
} from './insurance-flows/business-insurances/business-transport/constants';
import { BusinessVehicleService } from './insurance-flows/business-insurances/business-vehicle/business-vehicle.service';
import { BUSINESS_VEHICLE_FORM_BASE } from './insurance-flows/business-insurances/business-vehicle/constants';
import {
  BUSINESS_FLEET_INSURANCE_BASE,
  BUSINESS_FLEET_PROVIDERS,
} from './insurance-flows/business-insurances/business-vehicle-fleet/constants';
import { BUSINESS_CENTRALISATION_FORM_BASE } from './insurance-flows/business-insurances/centralisation/constants';
import { RC_COMPANY_FORM_BASE } from './insurance-flows/business-insurances/rc-company/constants';
import {
  BUSINESS_TECHNICAL_INSTALLATION_INSURANCE_BASE,
  BUSINESS_TECHNICAL_INSTALLATION_PROVIDERS,
} from './insurance-flows/business-insurances/technical-installation/constants';
import { CANCELLATION_BASE_URL } from './insurance-flows/cancellation-flow/constants';
import { CENTRALISATION_BASE_URL } from './insurance-flows/centralisation/constants';
import { CONSTRUCTION_INSURANCE_BASE_URL } from './insurance-flows/construction/constants';
import {
  HEALTH_INSURANCE_BASE_URL,
  OLD_HEALTH_INSURANCE_BASE_URL,
} from './insurance-flows/health/constants';
import {
  HOUSEHOLD_INSURANCE_BASE_URL,
  OLD_HOUSEHOLD_INSURANCE_BASE_URL,
} from './insurance-flows/household/household-constants';
import { LEGAL_PROTECTION_BASE_URL } from './insurance-flows/legal-protection/legal-protection-constants';
import { THIRD_PILLAR_INSURANCE_BASE } from './insurance-flows/third-pillar/constants';
import { TRAVEL_INSURANCE_BASE_URL } from './insurance-flows/travel/travel-constants';
import { VALUABLE_OBJECTS_BASE } from './insurance-flows/valuable-objects/constants';
import { VEHICLE_INSURANCE_SERVICE_TOKEN } from './insurance-flows/vehicle/base-vehicle.service';
import { VehicleService } from './insurance-flows/vehicle/vehicle.service';
import { VEHICLE_BASE_URL } from './insurance-flows/vehicle/vehicle-constants';

const routes: Routes = [
  {
    path: '',
    loadChildren: () =>
      import('./dashboard-v2/dashboard-v2.module').then((m) => m.DashboardV2Module),
  },
  {
    path: HEALTH_INSURANCE_BASE_URL,
    loadChildren: () =>
      import('./insurance-flows/health/maladie.module').then((m) => m.AssuranceMaladieModule),
    canActivate: [userLegalTypeGuard],
    data: {
      legalType: UserLegalType.PERSONAL,
      typeMismatchRedirection: `/`,
    },
  },
  {
    path: TRAVEL_INSURANCE_BASE_URL,
    loadChildren: () =>
      import('./insurance-flows/travel/travel.module').then((m) => m.TravelModule),
    canActivate: [userLegalTypeGuard],
    data: {
      legalType: UserLegalType.PERSONAL,
      typeMismatchRedirection: `/`,
    },
  },
  {
    path: CENTRALISATION_BASE_URL,
    loadChildren: () =>
      import('./insurance-flows/centralisation/centralisation.module').then(
        (m) => m.CentralisationModule,
      ),
    canActivate: [userLegalTypeGuard],
    data: {
      legalType: UserLegalType.PERSONAL,
      typeMismatchRedirection: `/${BUSINESS_CENTRALISATION_FORM_BASE}`,
      silentRedirection: true,
    },
  },
  // Since the vehicle flows (individual and company) will share the same abstract vehicle service, we provide the correct service here
  {
    path: VEHICLE_BASE_URL,
    loadChildren: () =>
      import('./insurance-flows/vehicle/vehicle.module').then((m) => m.VehicleModule),
    providers: [
      {
        provide: VEHICLE_INSURANCE_SERVICE_TOKEN,
        useExisting: VehicleService,
      },
    ],
    canActivate: [userLegalTypeGuard],
    data: {
      legalType: UserLegalType.PERSONAL,
      typeMismatchRedirection: `/${BUSINESS_VEHICLE_FORM_BASE}`,
      silentRedirection: true,
    },
  },
  {
    path: BUILDING_INSURANCE_BASE,
    loadChildren: () =>
      import('./insurance-flows/building/building.module').then((m) => m.BuildingModule),
    canActivate: [userLegalTypeGuard],
    data: {
      legalType: UserLegalType.PERSONAL,
      typeMismatchRedirection: `/${BUSINESS_BUILDING_FORM_URL}`,
      silentRedirection: true,
    },
    providers: [
      {
        provide: BUILDING_INSURANCE_SERVICE_TOKEN,
        useExisting: BuildingService,
      },
    ],
  },
  {
    path: LEGAL_PROTECTION_BASE_URL,
    loadChildren: () =>
      import('./insurance-flows/legal-protection/legal-protection.module').then(
        (m) => m.LegalProtectionModule,
      ),
    canActivate: [userLegalTypeGuard],
    data: {
      legalType: UserLegalType.PERSONAL,
      typeMismatchRedirection: `/`,
    },
  },
  {
    path: HOUSEHOLD_INSURANCE_BASE_URL,
    loadChildren: () =>
      import('./insurance-flows/household/household.module').then((m) => m.HouseholdModule),
    canActivate: [userLegalTypeGuard],
    data: {
      legalType: UserLegalType.PERSONAL,
      typeMismatchRedirection: `/`,
    },
  },
  {
    path: CLAIM_BASE_URL,
    loadChildren: () => import('./claims/claims.module').then((m) => m.ClaimsModule),
    canActivate: [userLegalTypeGuard],
    data: {
      legalType: UserLegalType.PERSONAL,
      typeMismatchRedirection: `/${BUSINESS_CLAIM_BASE_URL}`,
      silentRedirection: true,
    },
  },
  {
    path: CANCELLATION_BASE_URL,
    loadChildren: () =>
      import('./insurance-flows/cancellation-flow/cancellation-flow.module').then(
        (m) => m.CancellationFlowModule,
      ),
    canActivate: [userLegalTypeGuard],
    data: {
      legalType: UserLegalType.PERSONAL,
      typeMismatchRedirection: `/`,
    },
  },
  {
    path: CONSTRUCTION_INSURANCE_BASE_URL,
    loadChildren: () =>
      import('./insurance-flows/construction/construction.module').then(
        (m) => m.ConstructionModule,
      ),
    canActivate: [userLegalTypeGuard],
    data: {
      legalType: UserLegalType.PERSONAL,
      typeMismatchRedirection: `/`,
    },
  },
  {
    path: ANIMAL_INSURANCE_BASE,
    loadChildren: () =>
      import('./insurance-flows/animal/animal.module').then((m) => m.AnimalModule),
    canActivate: [userLegalTypeGuard],
    data: {
      legalType: UserLegalType.PERSONAL,
      typeMismatchRedirection: `/`,
    },
  },
  {
    path: VALUABLE_OBJECTS_BASE,
    loadChildren: () =>
      import('./insurance-flows/valuable-objects/valuable-objects.module').then(
        (m) => m.ValuableObjectsModule,
      ),
    canActivate: [userLegalTypeGuard],
    data: {
      legalType: UserLegalType.PERSONAL,
      typeMismatchRedirection: `/`,
    },
  },
  {
    path: THIRD_PILLAR_INSURANCE_BASE,
    loadChildren: () =>
      import('./insurance-flows/third-pillar/third-pillar.module').then((m) => m.ThirdPillarModule),
    canActivate: [userLegalTypeGuard],
    data: {
      legalType: UserLegalType.PERSONAL,
      typeMismatchRedirection: `/`,
    },
  },
  {
    path: RC_COMPANY_FORM_BASE,
    loadChildren: () =>
      import('./insurance-flows/business-insurances/rc-company/rc-company.module').then(
        (m) => m.RcCompanyModule,
      ),
    canActivate: [userLegalTypeGuard],
    data: {
      legalType: UserLegalType.COMPANY,
      typeMismatchRedirection: `/`,
    },
  },
  {
    path: BUSINESS_THINGS_FORM_BASE,
    loadChildren: () =>
      import('./insurance-flows/business-insurances/business-things/business-things.module').then(
        (m) => m.BusinessThingsModule,
      ),
    canActivate: [userLegalTypeGuard],
    data: {
      legalType: UserLegalType.COMPANY,
      typeMismatchRedirection: `/`,
    },
  },
  {
    path: BUSINESS_ACCIDENT_FORM_BASE,
    loadChildren: () =>
      import(
        './insurance-flows/business-insurances/business-accident/business-accident.module'
      ).then((m) => m.BusinessAccidentModule),
    canActivate: [userLegalTypeGuard],
    data: {
      legalType: UserLegalType.COMPANY,
      typeMismatchRedirection: `/`,
    },
  },
  {
    path: BUSINESS_HEALTH_FORM_BASE,
    loadChildren: () =>
      import('./insurance-flows/business-insurances/business-health/business-health.module').then(
        (m) => m.BusinessHealthModule,
      ),
    canActivate: [userLegalTypeGuard],
    data: {
      legalType: UserLegalType.COMPANY,
      typeMismatchRedirection: `/`,
    },
  },
  {
    path: BUSINESS_RETIREMENT_FORM_BASE,
    loadChildren: () =>
      import(
        './insurance-flows/business-insurances/business-retirement/business-retirement.module'
      ).then((m) => m.BusinessRetirementModule),
    canActivate: [userLegalTypeGuard],
    data: {
      legalType: UserLegalType.COMPANY,
      typeMismatchRedirection: `/`,
    },
  },
  {
    path: BUSINESS_CENTRALISATION_FORM_BASE,
    loadChildren: () =>
      import('./insurance-flows/business-insurances/centralisation/centralisation.module').then(
        (m) => m.CentralisationModule,
      ),
    canActivate: [userLegalTypeGuard],
    data: {
      legalType: UserLegalType.COMPANY,
      typeMismatchRedirection: `/${CENTRALISATION_BASE_URL}`,
      silentRedirection: true,
    },
  },
  {
    path: BUSINESS_VEHICLE_FORM_BASE,
    loadChildren: () =>
      import('./insurance-flows/business-insurances/business-vehicle/business-vehicle.module').then(
        (m) => m.BusinessVehicleModule,
      ),
    providers: [
      {
        provide: VEHICLE_INSURANCE_SERVICE_TOKEN,
        useExisting: BusinessVehicleService,
      },
    ],
    canActivate: [userLegalTypeGuard],
    data: {
      legalType: UserLegalType.COMPANY,
      typeMismatchRedirection: `/${VEHICLE_BASE_URL}`,
      silentRedirection: true,
    },
  },
  {
    path: BUSINESS_CLAIM_BASE_URL,
    loadChildren: () =>
      import('./claims-business/claims-business.module').then((m) => m.BusinessClaimsModule),
    canActivate: [userLegalTypeGuard],
    data: {
      legalType: UserLegalType.COMPANY,
      typeMismatchRedirection: `/${CLAIM_BASE_URL}`,
      silentRedirection: true,
    },
  },
  {
    path: BUSINESS_CYBERSECURITY_INSURANCE_BASE,
    loadChildren: () =>
      import(
        './insurance-flows/business-insurances/basic-shared-flow/basic-shared-flow.module'
      ).then((m) => m.BusinessBasicSharedFlowModule),
    canActivate: [userLegalTypeGuard],
    data: {
      legalType: UserLegalType.COMPANY,
      typeMismatchRedirection: '/',
    },
    providers: BUSINESS_CYBERSECURITY_PROVIDERS,
  },
  {
    path: BUSINESS_BUILDING_FORM_URL,
    loadChildren: () =>
      import(
        './insurance-flows/business-insurances/business-building/business-building.module'
      ).then((m) => m.BusinessBuildingModule),
    canActivate: [userLegalTypeGuard],
    data: {
      legalType: UserLegalType.COMPANY,
      typeMismatchRedirection: `/${BUILDING_INSURANCE_BASE}`,
      silentRedirection: true,
    },
    providers: [
      {
        provide: BUILDING_INSURANCE_SERVICE_TOKEN,
        useExisting: BusinessBuildingService,
      },
    ],
  },
  {
    path: BUSINESS_CONSTRUCTION_INSURANCE_BASE,
    loadChildren: () =>
      import(
        './insurance-flows/business-insurances/basic-shared-flow/basic-shared-flow.module'
      ).then((m) => m.BusinessBasicSharedFlowModule),
    canActivate: [userLegalTypeGuard],
    data: {
      legalType: UserLegalType.COMPANY,
      typeMismatchRedirection: `/${CONSTRUCTION_INSURANCE_BASE_URL}`,
      silentRedirection: true,
    },
    providers: BUSINESS_CONSTRUCTION_PROVIDERS,
  },
  {
    path: BUSINESS_CONSTRUCTION_WARRANTY_INSURANCE_BASE,
    loadChildren: () =>
      import(
        './insurance-flows/business-insurances/basic-shared-flow/basic-shared-flow.module'
      ).then((m) => m.BusinessBasicSharedFlowModule),
    canActivate: [userLegalTypeGuard],
    data: {
      legalType: UserLegalType.COMPANY,
      typeMismatchRedirection: '/',
    },
    providers: BUSINESS_CONSTRUCTION_WARRANTY_PROVIDERS,
  },
  {
    path: BUSINESS_RC_PROFESSIONAL_INSURANCE_BASE,
    loadChildren: () =>
      import(
        './insurance-flows/business-insurances/basic-shared-flow/basic-shared-flow.module'
      ).then((m) => m.BusinessBasicSharedFlowModule),
    canActivate: [userLegalTypeGuard],
    data: {
      legalType: UserLegalType.COMPANY,
      typeMismatchRedirection: '/',
    },
    providers: BUSINESS_RC_PROFESSIONAL_PROVIDERS,
  },
  {
    path: BUSINESS_RENTAL_INSURANCE_BASE,
    loadChildren: () =>
      import(
        './insurance-flows/business-insurances/basic-shared-flow/basic-shared-flow.module'
      ).then((m) => m.BusinessBasicSharedFlowModule),
    canActivate: [userLegalTypeGuard],
    data: {
      legalType: UserLegalType.COMPANY,
      typeMismatchRedirection: '/',
    },
    providers: BUSINESS_RENTAL_PROVIDERS,
  },
  {
    path: BUSINESS_TRANSPORT_INSURANCE_BASE,
    loadChildren: () =>
      import(
        './insurance-flows/business-insurances/basic-shared-flow/basic-shared-flow.module'
      ).then((m) => m.BusinessBasicSharedFlowModule),
    canActivate: [userLegalTypeGuard],
    data: {
      legalType: UserLegalType.COMPANY,
      typeMismatchRedirection: '/',
    },
    providers: BUSINESS_TRANSPORT_PROVIDERS,
  },
  {
    path: BUSINESS_LEGAL_PROTECTION_INSURANCE_BASE,
    loadChildren: () =>
      import(
        './insurance-flows/business-insurances/basic-shared-flow/basic-shared-flow.module'
      ).then((m) => m.BusinessBasicSharedFlowModule),
    canActivate: [userLegalTypeGuard],
    data: {
      legalType: UserLegalType.COMPANY,
      typeMismatchRedirection: '/',
    },
    providers: BUSINESS_LEGAL_PROTECTION_PROVIDERS,
  },
  {
    path: BUSINESS_FLEET_INSURANCE_BASE,
    loadChildren: () =>
      import(
        './insurance-flows/business-insurances/basic-shared-flow/basic-shared-flow.module'
      ).then((m) => m.BusinessBasicSharedFlowModule),
    canActivate: [userLegalTypeGuard],
    data: {
      legalType: UserLegalType.COMPANY,
      typeMismatchRedirection: '/',
    },
    providers: BUSINESS_FLEET_PROVIDERS,
  },
  {
    path: BUSINESS_TECHNICAL_INSTALLATION_INSURANCE_BASE,
    loadChildren: () =>
      import(
        './insurance-flows/business-insurances/basic-shared-flow/basic-shared-flow.module'
      ).then((m) => m.BusinessBasicSharedFlowModule),
    canActivate: [userLegalTypeGuard],
    data: {
      legalType: UserLegalType.COMPANY,
      typeMismatchRedirection: '/',
    },
    providers: BUSINESS_TECHNICAL_INSTALLATION_PROVIDERS,
  },
  {
    path: 'user',
    redirectTo: '',
    pathMatch: 'full',
  },
  {
    path: OLD_HOUSEHOLD_INSURANCE_BASE_URL,
    redirectTo: HOUSEHOLD_INSURANCE_BASE_URL,
  },
  {
    path: OLD_BUILDING_INSURANCE_BASE,
    redirectTo: BUILDING_INSURANCE_BASE,
  },
  {
    path: OLD_HEALTH_INSURANCE_BASE_URL,
    redirectTo: HEALTH_INSURANCE_BASE_URL,
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { scrollPositionRestoration: 'enabled' })],
  exports: [RouterModule],
})
export class AppRoutingModule {}
